import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { LocationSelect } from "@/components/LocationSelect";
import { WineSuggestions, AddToExisting } from "@/components/WineSuggestions";
import { Field, OptionSelect, OriginFields, ConditionFields, DocumentField, BOTTLE_SIZES, EXTRA_DEFAULTS, ORIGIN_FIELDS } from "@/components/WineFormSections";

const empty = { name: "", producer: "", vintage: "", color: "", region: "", purchase_date: "", supplier: "", purchase_price: "", notes: "", bottles: 1, box_number: "", location_id: "", drink_from: "", drink_until: "", ...EXTRA_DEFAULTS };
const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
const EXTRA_KEYS = Object.keys(EXTRA_DEFAULTS);
const parseVintage = (v) => {
  const s = String(v ?? "").trim().toUpperCase();
  if (!s || s === "NV") return null;
  return /^\d{4}$/.test(s) ? Number(s) : undefined;
};

export const WineFormDialog = ({ open, onOpenChange, wine, onSaved, catalog, defaultLocation, inCellar = 0 }) => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);
  const [hideSuggest, setHideSuggest] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const isEdit = !!wine;

  useEffect(() => {
    if (open) {
      setExisting(null);
      setHideSuggest(!!catalog);
      setDocFile(null);
      if (wine) {
        setF({ ...empty, ...wine, vintage: wine.vintage ?? "NV", purchase_price: wine.purchase_price ?? "", drink_from: wine.drink_from ?? "", drink_until: wine.drink_until ?? "", bottles: inCellar, box_number: "", location_id: "" });
      } else if (catalog) {
        const { id, display_name, ...fields } = catalog;
        setF({ ...empty, ...fields, location_id: defaultLocation || "" });
      } else {
        setF({ ...empty, location_id: defaultLocation || "" });
      }
    }
  }, [open, wine, catalog, defaultLocation]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const applyTemplate = (w) => {
    const { name, producer, color, region, supplier, purchase_price, drink_from, drink_until } = w;
    const origin = Object.fromEntries([...ORIGIN_FIELDS.map(([k]) => k), "bottle_size", "catalog_name"].map((k) => [k, w[k] || EXTRA_DEFAULTS[k]]));
    setF({ ...empty, ...origin, name, producer, color: color || "", region: region || "", supplier: supplier || "", purchase_price: purchase_price ?? "", drink_from: drink_from ?? "", drink_until: drink_until ?? "", location_id: defaultLocation || "" });
    setHideSuggest(true);
    toast.message("Details copied — set the new vintage");
  };
  const applyCatalog = (c) => {
    const { id, display_name, ...fields } = c;
    setF({ ...f, ...fields });
    setHideSuggest(true);
    toast.message("Wine details filled from the database — add vintage, size and bottles");
  };
  const savedExisting = () => {
    onOpenChange(false);
    onSaved?.();
  };

  const save = async (e) => {
    e.preventDefault();
    const vintage = parseVintage(f.vintage);
    if (vintage === undefined) return toast.error("Vintage must be a year (e.g. 2018) or NV");
    if (isEdit && Number(f.bottles) < inCellar) return toast.error("To remove bottles, use Actions on each bottle");
    setBusy(true);
    const body = {
      ...Object.fromEntries(EXTRA_KEYS.map((k) => [k, f[k] || ""])),
      name: f.name, producer: f.producer, color: f.color, region: f.region, purchase_date: f.purchase_date, supplier: f.supplier, notes: f.notes,
      vintage,
      purchase_price: numOrNull(f.purchase_price),
      drink_from: numOrNull(f.drink_from),
      drink_until: numOrNull(f.drink_until),
    };
    try {
      if (isEdit) {
        await api.put(`/wines/${wine.id}`, body);
        const extra = (Number(f.bottles) || 0) - inCellar;
        if (extra > 0) await api.post(`/wines/${wine.id}/bottles`, { count: extra, box_number: f.box_number, location_id: f.location_id || null });
      } else {
        const { data: created } = await api.post("/wines", { ...body, bottles: Number(f.bottles) || 0, box_number: f.box_number, location_id: f.location_id || null });
        if (docFile) {
          const fd = new FormData();
          fd.append("file", docFile);
          await api.post(`/wines/${created.id}/document`, fd).catch((err) => toast.error(`Wine saved, but the document failed: ${errMsg(err)}`));
        }
      }
      toast.success(isEdit ? "Wine updated" : "Wine added to the cellar");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#161214] border-[#2E252A]" data-testid="wine-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Edit wine" : existing ? "Add bottles" : "Add a wine"}</DialogTitle>
        </DialogHeader>
        {existing ? <AddToExisting wine={existing} defaultLocation={defaultLocation} onBack={() => setExisting(null)} onSaved={savedExisting} /> : (
        <form onSubmit={save} className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Wine *" className="col-span-2"><Input required value={f.name} onChange={(e) => { setHideSuggest(false); set("name")(e); }} autoComplete="off" placeholder={isEdit ? "" : "Type 3 letters to search the wine database"} data-testid="wine-name-input" /></Field>
          {!isEdit && !hideSuggest && <WineSuggestions query={f.name} onAddBottles={setExisting} onTemplate={applyTemplate} onCatalog={applyCatalog} />}
          <Field label="Producer"><Input value={f.producer} onChange={set("producer")} data-testid="wine-producer-input" /></Field>
          <Field label="Vintage"><Input value={f.vintage} onChange={set("vintage")} placeholder="2018 or NV" data-testid="wine-vintage-input" /></Field>
          <Field label="Serial number"><Input value={f.serial_number} onChange={set("serial_number")} placeholder="As printed on the label or back label" data-testid="wine-serial-number-input" /></Field>
          <Field label="Bottle size"><OptionSelect value={f.bottle_size} onChange={(v) => setF({ ...f, bottle_size: v })} options={BOTTLE_SIZES} allowEmpty={false} testId="wine-bottle-size-select" /></Field>
          <Field label="Colour"><Input value={f.color} onChange={set("color")} placeholder="Red, White, Rosé…" data-testid="wine-color-input" /></Field>
          {!isEdit && <Field label="Purchase date"><Input type="date" value={f.purchase_date} onChange={set("purchase_date")} data-testid="wine-purchase-date-input" /></Field>}
          {!isEdit && <DocumentField wine={null} file={docFile} setFile={setDocFile} onChanged={onSaved} />}
          {!isEdit && <Field label="Supplier"><Input value={f.supplier} onChange={set("supplier")} data-testid="wine-supplier-input" /></Field>}
          {!isEdit && <Field label="Purchase price (per bottle)"><Input type="number" step="0.01" value={f.purchase_price} onChange={set("purchase_price")} data-testid="wine-purchase-price-input" /></Field>}
          <Field label="Drink from (year)"><Input type="number" value={f.drink_from} onChange={set("drink_from")} placeholder="2025" data-testid="wine-drink-from-input" /></Field>
          <Field label="Drink until (year)"><Input type="number" value={f.drink_until} onChange={set("drink_until")} placeholder="2035" data-testid="wine-drink-until-input" /></Field>
          {isEdit && (
            <Field label="Number of bottles in the cellar">
              <Input type="number" min={inCellar} max={inCellar + 500} value={f.bottles} onChange={set("bottles")} data-testid="wine-bottles-input" />
              {Number(f.bottles) > inCellar && <p className="text-xs text-[#D4AF37] mt-1" data-testid="wine-bottles-extra">{Number(f.bottles) - inCellar} new bottle{Number(f.bottles) - inCellar > 1 ? "s" : ""} will be created with their own barcodes</p>}
              {Number(f.bottles) < inCellar && <p className="text-xs text-[#E5A93C] mt-1">To remove bottles, use Actions on each bottle (Sale, Consumed, Lost…)</p>}
            </Field>
          )}
          {(!isEdit || Number(f.bottles) > inCellar) && (
            <>
              {!isEdit && <Field label="Number of bottles"><Input type="number" min="0" max="500" value={f.bottles} onChange={set("bottles")} data-testid="wine-bottles-input" /></Field>}
              <Field label="Box number (optional)"><Input value={f.box_number} onChange={set("box_number")} placeholder="BX-012" data-testid="wine-box-input" /></Field>
              <Field label="Location"><LocationSelect value={f.location_id} onChange={(v) => setF((p) => ({ ...p, location_id: v }))} testId="wine-location-select" allowCreate allowPending /></Field>
            </>
          )}
          <OriginFields f={f} set={set} />
          {isEdit ? <p className="col-span-2 text-xs text-[#A89F91] rounded-md border border-[#2E252A] px-3 py-2" data-testid="wine-per-bottle-note">Supplier, purchase date, price, document and bottle condition are kept per bottle — edit them in the Bottles list.</p> : <ConditionFields f={f} setF={setF} />}
          <Field label="Notes" className="col-span-2"><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="wine-notes-input" /></Field>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="wine-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="wine-save-button">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
